import { PuliziaCard } from "@/components/PuliziaCard";
import { useEffect, useMemo, useState } from "react";
import { ClienteView } from "@/components/cliente/ClienteView";
import { ClienteLinks } from "@/components/cliente/ClienteLinks";
import { staffSource } from "@/components/cliente/clienteApi";
import { PlantComponents } from "./PlantComponents";
import { EvStationsCard } from "./EvStationsCard";import { TigoSystemsCard } from "@/components/tigo/TigoSystemsCard";import { ArrowLeft, Eye, Loader2, BatteryMedium, Zap, Sun, AlertTriangle, CheckCircle2, Cpu, Sparkles, Activity, Home, MapPin } from "lucide-react";
import { AlarmAnalysis } from "./AlarmAnalysis";
import { BehaviorAnalysis } from "./BehaviorAnalysis";
import { ConsumptionAnalysis } from "./ConsumptionAnalysis";
import { ChecklistButton } from "./Checklist";
import { AlarmLog } from "./AlarmLog";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlantChart } from "./PlantChart";
import { InverterList } from "./InverterList";
import { ConfigMisure } from "./ConfigMisure";
import { PartnerPicker } from "./PartnerPicker";
import { AddressDialog } from "./AddressDialog";
import { toast } from "sonner";
import { LIGHT, nf, kw, ago, dtt, DIREZ } from "./shared";
import { useVestaView } from "@/pages/vesta/vestaStore";

const Stat = ({ icon: Icon, label, value, testid, color }) => (
  <div className="rounded-xl border border-neutral-800 p-3">
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-neutral-400"><Icon className="h-3.5 w-3.5" />{label}</div>
    <div className="mt-1 font-mono text-lg font-black" style={{ color }} data-testid={testid}>{value}</div>
  </div>
);

const Alarms = ({ list, onOpen }) => (
  <div className="space-y-2" data-testid="plant-alarms">
    <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Allarmi <span className="normal-case tracking-normal text-neutral-600">· clicca per l'analisi AI</span></div>
    {!list.length ? <p className="flex items-center gap-2 text-sm text-neutral-500"><CheckCircle2 className="h-4 w-4 text-green-500" />Nessun allarme registrato</p>
      : list.map((a, i) => (
        <button type="button" key={i} onClick={() => onOpen(a)} className="flex w-full items-center gap-3 rounded-lg border border-neutral-800 px-3 py-2 text-left text-sm transition-colors hover:border-neutral-500" data-testid={`plant-alarm-${i}`}>
          <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: a.active ? (a.level === 2 ? "#ef4444" : "#eab308") : "#737373" }} />
          <span className="font-mono text-xs text-neutral-400">{a.code_n ? `E${a.code_n}` : a.code}{a.sigla ? ` · ${a.sigla}` : ""}</span>
          <span className="min-w-0 flex-1 truncate text-white">{a.desc}{a.occorrenze > 1 ? <span className="text-neutral-500"> · {a.occorrenze} volte</span> : ""}
            {a.sn && <span className="block truncate text-[11px] text-neutral-500" data-testid={`plant-alarm-inv-${i}`}>{a.model} · SN {a.sn}</span>}</span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${a.active ? "bg-red-500/15 text-red-400" : "bg-neutral-800 text-neutral-400"}`}>{a.active ? "Attivo" : "Rientrato"}</span>
          <span className="hidden shrink-0 text-xs text-neutral-500 sm:inline">{dtt(a.at)}</span>
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-neutral-500" />
        </button>
      ))}
  </div>
);

const Assign = ({ p, reload }) => {
  const pick = async (partner_id) => {
    try { await api.put(`/monitoring/plants/${encodeURIComponent(p.id)}/partner`, { partner_id }); toast.success(partner_id ? "Impianto abbinato" : "Abbinamento rimosso"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-800 px-3 py-2 text-xs text-neutral-400" data-testid="plant-assign">
      <span>Account FIER <b className="font-mono text-neutral-200">{p.account}</b>{p.parent_id && <> · installatore <b className="font-mono text-neutral-200">{p.parent_id}</b></>}</span>
      <span className="ml-auto">{p.partner_override === DIREZ ? "Associato a:" : "Partner:"}</span>
      <PartnerPicker value={p.partner_override || null} label={p.partner_name} onPick={pick} testid="plant-partner-picker" />
      {!p.partner_override && p.partner_id && <span className="text-neutral-500">{p.partner_id === DIREZ ? `(automatico: account ${p.parent_id || "torriadmin"})` : `(da account installatore: ${p.partner_name})`}</span>}
    </div>
  );
};

const Address = ({ p, base, params, reload, accent }) => {
  const [open, setOpen] = useState(false);
  const stato = p.posizione_esatta ? "Posizione esatta sulla mappa" : p.posizione_approssimativa ? "Posizione approssimativa (comune indicato a Vesta)" : "Posizione approssimativa (sede Partner)";
  return (
    <div className="space-y-1.5 rounded-xl border border-neutral-800 px-3 py-2" data-testid="plant-address">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-neutral-400"><MapPin className="h-3.5 w-3.5" />Indirizzo impianto
        <span className={`ml-auto normal-case tracking-normal ${p.posizione_esatta ? "text-green-500" : "text-amber-400"}`} data-testid="plant-address-status">{stato}</span></div>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm text-neutral-200" data-testid="plant-address-value">{p.indirizzo || <span className="text-neutral-500">Nessun indirizzo indicato</span>}</span>
        <button type="button" onClick={() => setOpen(true)} data-testid="plant-address-open"
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold text-black transition-opacity" style={{ background: accent }}>
          <MapPin className="h-3.5 w-3.5" />{p.posizione_esatta ? "Modifica indirizzo" : "Indica sulla mappa"}</button>
      </div>
      {open && <AddressDialog p={p} base={base} params={params} reload={reload} accent={accent} open={open} onClose={() => setOpen(false)} />}
    </div>
  );
};

// spia pulizia pannelli (testi per l'installatore); ricontrolla finché l'analisi è in corso
const Pulizia = ({ base, params, id, chiave }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let t, vivo = true;
    const get = () => api.get(`${base}/plants/${encodeURIComponent(id)}/pulizia`, { params }).then((r) => {
      if (!vivo) return;
      setD(r.data);
      if (r.data.spia === "in_corso") t = setTimeout(get, 10000);
    }).catch(() => {});
    get();
    return () => { vivo = false; clearTimeout(t); };
  }, [base, id, chiave]); // eslint-disable-line react-hooks/exhaustive-deps
  return <PuliziaCard d={d} dark />;
};

const ClientePreview = ({ base, params, p, onClose }) => {
  const src = useMemo(() => staffSource(base, p.id, params), [base, p.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="left-0 top-0 h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 overflow-hidden rounded-none border-0 bg-slate-50 p-0 sm:rounded-none" data-testid="vista-cliente-dialog" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Vista Cliente</DialogTitle>
        <button type="button" onClick={onClose} data-testid="vista-cliente-indietro"
          className="absolute left-4 top-4 z-[80] inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-2 text-xs font-bold text-white shadow-lg transition-transform hover:scale-105">
          <ArrowLeft className="h-4 w-4" />Torna indietro</button>
        <div className="h-full overflow-y-auto"><ClienteView src={src}
          impostazioniInstallatore={<ClienteLinks base={base} plantId={p.id} params={params} nome={p.nome} />}
          header={
          <div className="mx-auto max-w-2xl space-y-3 p-4 pb-0">
            <div className="flex items-center gap-2 pl-[150px]">
              <p className="min-w-0 flex-1 truncate rounded-full bg-amber-100 px-3 py-2 text-center text-xs font-bold text-amber-800" data-testid="vista-cliente-anteprima">Anteprima: è ciò che vede il cliente finale</p>
            </div>
          </div>} /></div>
      </DialogContent>
    </Dialog>
  );
};

const Plain = ({ children }) => <>{children}</>;
const PlainBox = ({ children }) => <div className="space-y-5 rounded-2xl border border-neutral-800 bg-neutral-950 p-5 text-white" data-testid="plant-detail-inline">{children}</div>;

export function PlantDetail({ base, params, plantId, onClose, accent, canAssign, onChanged, readOnly = false }) {
  const [D, DC, DH, DT] = readOnly ? [Plain, PlainBox, "div", "div"] : [Dialog, DialogContent, DialogHeader, DialogTitle];
  const [p, setP] = useState(null);
  const [al, setAl] = useState(null);
  const [ver, setVer] = useState(0);
  const [beh, setBeh] = useState(false);
  const [cons, setCons] = useState(false);
  const [cli, setCli] = useState(false);
  const load = () => api.get(`${base}/plants/${encodeURIComponent(plantId)}`, { params }).then((r) => setP(r.data)).catch(() => onClose());
  useEffect(() => {
    if (!plantId) return;
    setP(null);
    load();
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("vista") === "cliente") {
        setCli(true);
        q.delete("vista");
        window.history.replaceState({}, "", window.location.pathname + (q.toString() ? "?" + q.toString() : ""));
      }
    } catch { /* ignore */ }
  }, [base, plantId]); // eslint-disable-line react-hooks/exhaustive-deps
  const L = p ? LIGHT[p.light] : null;
  useVestaView(!!plantId && !!p, p && { scheda: "impianto (Torri Monitoring)", nome: p.nome, comune: p.comune, stato: p.stato, allarmi_attivi: p.allarmi_attivi });
  return (
    <D open={!!plantId} onOpenChange={(v) => !v && onClose()}>
      <DC className="max-h-[90vh] max-w-3xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="plant-detail-dialog" aria-describedby={undefined}>
        {!p ? <><DT className="sr-only">Impianto</DT><Loader2 className="h-6 w-6 animate-spin" style={{ color: accent }} /></> : (
          <div className="space-y-5">
            <DH>
              <DT className="flex items-center gap-3 font-heading text-xl">
                <span className="h-3 w-3 rounded-full" style={{ background: L.color, boxShadow: `0 0 12px ${L.color}` }} />
                <span data-testid="plant-detail-name">{p.nome}</span>
                <span className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase" style={{ borderColor: L.color, color: L.color }} data-testid="plant-detail-status">{L.label}</span>
                {!p.mock && <button type="button" onClick={() => setCli(true)} data-testid="vista-cliente-btn"
                  className="ml-auto mr-6 inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-800 transition-transform hover:scale-105"><Eye className="h-3.5 w-3.5" />Vista Cliente</button>}
              </DT>
              <p className="text-xs text-neutral-400">{[p.comune, p.provincia && `(${p.provincia})`, `${nf(p.kwp, 2)} kWp`, p.partner_id === DIREZ ? "Direzionale · Divisione Impianti Torri" : `Partner: ${p.partner_name || "da abbinare"}`].filter(Boolean).join(" · ")}</p>
            </DH>
            {canAssign && !p.mock && <Assign p={p} reload={() => { load(); onChanged(); }} />}
            {!p.mock && !readOnly && <Address key={p.indirizzo || ""} p={p} base={base} params={params} reload={() => { load(); onChanged?.(); }} accent={accent} />}
            {!p.mock && <Pulizia base={base} params={params} id={p.id} chiave={p.indirizzo || ""} />}
            {p.cloud_error && <p className="text-xs text-amber-300" data-testid="plant-cloud-error">Cloud FIER momentaneamente non raggiungibile: dati dell'ultima sincronizzazione.</p>}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat icon={Zap} label="Potenza ora" value={kw(p.pac_w)} testid="plant-detail-pac" color={accent} />
              <Stat icon={Sun} label="Oggi" value={`${nf(p.e_oggi_kwh)} kWh`} testid="plant-detail-today" color="#fff" />
              <Stat icon={Sun} label="Totale" value={`${nf(p.e_totale_kwh / 1000, 2)} MWh`} testid="plant-detail-total" color="#fff" />
              <Stat icon={BatteryMedium} label="Batteria SOC" value={p.soc == null ? "—" : `${p.soc}%`} testid="plant-detail-soc" color="#22c55e" />
            </div>
            {!p.mock && !readOnly && (
              <button type="button" onClick={() => setBeh(true)} data-testid="behavior-open-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full border px-5 py-2.5 text-center text-sm font-bold leading-snug transition-colors hover:bg-white/5" style={{ borderColor: accent, color: accent }}>
                <Activity className="h-4 w-4 shrink-0" /><span>Analizza comportamento e batteria <span className="whitespace-nowrap">(anno vs anni precedenti)</span></span>
              </button>
            )}
            {!p.mock && !readOnly && (
              <button type="button" onClick={() => setCons(true)} data-testid="consumi-open-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-sky-400/60 py-2.5 text-sm font-bold text-sky-300 transition-colors hover:bg-white/5">
                <Home className="h-4 w-4" />Analizza consumi dell'utenza
              </button>
            )}
            {!p.mock && !readOnly && <AlarmLog plantId={p.id} />}
            {!p.mock && !readOnly && <ChecklistButton base={base} params={params} plant={p} accent={accent} />}
            <ConsumptionAnalysis base={base} params={params} plantId={p.id} open={cons} onClose={() => setCons(false)} accent={accent} />
            <BehaviorAnalysis base={base} params={params} plantId={p.id} open={beh} onClose={() => setBeh(false)} accent={accent} />
            <PlantChart base={base} plantId={p.id} params={params} accent={accent} inverters={p.inverters} ver={ver} />
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-400" data-testid="plant-detail-device">
              {(p.inverters?.length || 0) < 2 && <span className="flex items-center gap-1"><Cpu className="h-3.5 w-3.5" />{p.inverter || "Inverter in lettura"}</span>}
              {(p.inverters?.length || 0) < 2 && p.sn && <span>SN <b className="font-mono text-neutral-200">{p.sn}</b></span>}
              <span>Batteria {p.batteria_kwh ? `${nf(p.batteria_kwh, 2)} kWh` : p.batteria ? `SOH ${p.batteria.soh}% · ${p.batteria.temp}°C · ${p.batteria.modo || ""}` : "assente"}</span>
              {p.installato_il && <span>Installato il {new Date(p.installato_il).toLocaleDateString("it-IT")}</span>}
              <span>Ultimo dato {ago(p.last_update)}</span>
            </div>
            <PlantComponents list={p.componenti} />
            <InverterList p={p} />
            {base === "/monitoring" && !p.mock && <EvStationsCard plantId={p.id} />}
            {base === "/monitoring" && !p.mock && <TigoSystemsCard plantId={p.id} />}
            {base === "/monitoring" && !readOnly && p.source !== "mock" && !p.mock && <ConfigMisure plantId={p.id} onChange={() => setVer((v) => v + 1)} />}
            <Alarms list={p.allarmi || []} onOpen={setAl} />
            {cli && <ClientePreview base={base} params={params} p={p} onClose={() => setCli(false)} />}
            <AlarmAnalysis base={base} params={params} plantId={p.id} alarm={al} onClose={() => setAl(null)} accent={accent} plantName={p.nome} />
          </div>
        )}
      </DC>
    </D>
  );
}
